/**
 * GoedkeurenModal — shared confirm-before-billing dialog.
 *
 * Used by:
 *  - FotoReviewProjectView (foto-controle page): collector comes from the review
 *    endpoint (ReviewCollector shape, which satisfies this interface directly).
 *  - CollectorPhotoPanel (dashboard): caller builds the minimal shape from the
 *    Collector store record + a separately resolved installateur prop.
 *
 * Branching:
 *  - installateur present → "Goedkeuren & op PO zetten" flow; calls onAddToPo.
 *  - installateur null    → plain approve; calls onApproveNoOa.
 */
import { useState } from 'react';
import { ApiError } from '@/api/errors';

export interface GoedkeurenCollector {
  id: number;
  name: string;
  systeem?: string | null;
  m2?: number;
  kringen?: Array<{
    kringnummer: number;
    system?: string | null;
    legpatroon?: number | null;
  }>;
  // isSubcontractor: false ⇒ TD own worker — never billed onto a PO (plain approve only).
  // Undefined (dashboard panel) is treated as billable; the server still guards own workers.
  installateur: { id: number; name: string; isSubcontractor?: boolean } | null;
  // Supplier-tier preview (foto-controle only — absent in the dashboard panel). When present,
  // the modal shows the pricing levers (placement + 0-200/200+ tier override).
  basisM2?: number;
  tier?: 'under200' | 'from200';
  tierScope?: 'materiaallijst' | 'project';
  tierLabel?: string;
  /**
   * Fase-level "Collectoren geleverd" signal (foto-controle only — absent in dashboard panel).
   * When flagged is true: pre-tick the geplaatst lever and show an amber warning.
   * Soft signals (source=project_flagged / hint) are shown in the card badge, not the modal.
   */
  geleverd?: {
    flagged: boolean;
    source: 'exact' | 'project_flagged' | 'none';
    confidence: 'high' | 'low';
    faseName: string | null;
    faseNote: string | null;
    hint: boolean;
  };
  /**
   * Werf-level "Collectoren geleverd" toggle (dashboard panel only). Coarser than `geleverd`:
   * it is the project-wide aggregate the dashboard already holds, NOT a per-collector fase
   * resolution — so it drives an informational notice only. The panel sends no `geplaatst`
   * lever; the server re-resolves per collector and applies the minprijs itself.
   */
  geleverdWerf?: boolean;
}

export interface GoedkeurenModalProps {
  collector: GoedkeurenCollector;
  onCancel: () => void;
  /** Called when installateur is null — plain approve without PO billing. May be async. */
  onApproveNoOa: () => void | Promise<void>;
  /**
   * Called when installateur is present — bills onto PO + flips status.
   * Receives the chosen pricing levers (placement + tier override). `geplaatst` is omitted
   * entirely when this modal has no pricing levers (dashboard panel): that "no opinion" stance
   * lets the server apply the minprijs itself on a "Collectoren geleverd" fase instead of
   * rejecting an approval this caller cannot answer. Sending `false` would mean "bill full".
   * Throws ApiError on failure (COLLECTOR_ALREADY_ON_PO / NO_INSTALLATEUR / generic).
   * Parent must close the modal on success.
   */
  onAddToPo: (opts: { geplaatst?: boolean; tier?: 'under200' | 'from200' }) => Promise<void>;
  /**
   * 409 COLLECTOR_ALREADY_ON_PO: hand the Dutch message to the parent.
   * In FotoReview the parent owns the notice banner (survives card unmount on refetch).
   * In the panel the parent sets a local inline notice.
   */
  onAlreadyOnPo: (message: string) => void;
}

export function GoedkeurenModal({
  collector,
  onCancel,
  onApproveNoOa,
  onAddToPo,
  onAlreadyOnPo,
}: GoedkeurenModalProps) {
  const installateur = collector.installateur;
  // Bill to PO only for external subcontractors. Own workers (isSubcontractor === false) and
  // collectors with no installateur get plain approval instead.
  const canBillToPo = installateur !== null && installateur.isSubcontractor !== false;
  const isOwnWorker = installateur !== null && installateur.isSubcontractor === false;
  // Pricing levers are only shown when the tier preview is supplied (foto-controle).
  const showPricingLevers = canBillToPo && collector.tier !== undefined;
  // Pre-tick when the fase is authoritatively flagged as client-placed ("Collectoren geleverd").
  // Reviewer can still untick; the warning explains the default.
  const [geplaatst, setGeplaatst] = useState(!!collector.geleverd?.flagged);
  const [tier, setTier] = useState<'under200' | 'from200'>(collector.tier ?? 'under200');
  const [submitting, setSubmitting] = useState(false);
  const [resultBanner, setResultBanner] = useState<{
    type: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  // "Enkel goedkeuren" — approve the photos without billing onto the PO. Only surfaced as a
  // separate button when canBillToPo is true (otherwise the primary button already does this).
  // Guarded by a confirm: once approved the collector leaves both review surfaces, so there is
  // no portal path to bill it onto the PO afterwards — a mis-click would silently drop billing.
  const handleApproveOnly = async () => {
    const ok = window.confirm(
      `Foto's van ${collector.name} enkel goedkeuren, zonder op de PO van ${installateur!.name} te zetten?\n\n` +
        'Deze collector kan daarna niet meer via de portal op de PO worden gezet.',
    );
    if (!ok) return;
    setSubmitting(true);
    setResultBanner(null);
    try {
      await onApproveNoOa();
    } catch (err) {
      setResultBanner({
        type: 'error',
        message: err instanceof Error ? err.message : 'Goedkeuren mislukt',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setResultBanner(null);
    try {
      if (!canBillToPo) {
        // No PO: own worker or no installateur → plain approve. Await so `finally` keeps the
        // button disabled until the write settles (the dashboard panel's handler is async).
        await onApproveNoOa();
        return;
      }
      // Only send a tier override when the reviewer changed it from the computed default.
      const tierOverride = showPricingLevers && tier !== collector.tier ? tier : undefined;
      // No levers ⇒ omit `geplaatst` (no opinion). See the onAddToPo doc above.
      await onAddToPo({ ...(showPricingLevers ? { geplaatst } : {}), tier: tierOverride });
      // On success the caller closes the modal and shows the notice.
    } catch (err) {
      if (err instanceof ApiError && err.code === 'COLLECTOR_ALREADY_ON_PO') {
        // Parent owns the notice (survives card unmount in FotoReview; inline in panel).
        onAlreadyOnPo('Deze collector staat al op een PO.');
      } else if (err instanceof ApiError && err.code === 'NO_INSTALLATEUR') {
        setResultBanner({
          type: 'error',
          message: 'Geen onderaannemer gevonden voor deze collector.',
        });
      } else {
        setResultBanner({
          type: 'error',
          message: err instanceof Error ? err.message : 'Goedkeuren mislukt',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={submitting ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="goedkeuren-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-gray-100">
          <div>
            <h3
              id="goedkeuren-modal-title"
              className="text-lg font-extrabold text-gray-900"
            >
              {canBillToPo ? 'Goedkeuren & op PO zetten' : 'Goedkeuren'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{collector.name}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
            aria-label="Sluiten"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {/* Summary row */}
          <div className="flex flex-wrap gap-2 mb-4 text-sm">
            {collector.systeem && (
              <span className="px-2 py-0.5 bg-td-blue/10 text-td-blue rounded-full font-medium capitalize">
                {collector.systeem}
              </span>
            )}
            {collector.m2 !== undefined && collector.m2 > 0 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                {collector.m2.toFixed(1)} m²
              </span>
            )}
          </div>

          {/* Compact kringen list */}
          {collector.kringen && collector.kringen.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 mb-1">
                Kringen ({collector.kringen.length})
              </h4>
              <div className="text-xs text-gray-700 space-y-0.5">
                {collector.kringen.map((k) => (
                  <div key={k.kringnummer}>
                    <span className="font-medium">{k.kringnummer}</span>
                    {k.system && (
                      <span className="text-gray-500"> · {k.system}</span>
                    )}
                    {k.legpatroon ? (
                      <span className="text-gray-500"> · LP {k.legpatroon}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PO / own-worker / no-OA message */}
          {canBillToPo ? (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
              Wordt toegevoegd aan de lopende PO van{' '}
              <span className="font-semibold">{installateur!.name}</span>.
            </div>
          ) : isOwnWorker ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              Geplaatst door <span className="font-semibold">{installateur!.name}</span> (eigen werknemer) — wordt enkel goedgekeurd, geen PO.
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              Deze collector heeft geen onderaannemer — wordt enkel goedgekeurd.
            </div>
          )}

          {/* Collectoren geleverd warning — authoritative flag only (soft signals stay in the card badge). */}
          {canBillToPo && collector.geleverd?.flagged && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              Deze fase is gemarkeerd als &ldquo;Collectoren geleverd&rdquo;
              {collector.geleverd.faseName ? ` (${collector.geleverd.faseName})` : ''}
              {' '}— mogelijk al door de klant geplaatst.
              {showPricingLevers && (
                <> &ldquo;Reeds geplaatst&rdquo; is voorgevinkt (minprijs). Vink uit om toch volledig te billen.</>
              )}
              {collector.geleverd.faseNote && (
                <div className="mt-1 text-amber-800">{collector.geleverd.faseNote}</div>
              )}
            </div>
          )}

          {/* Werf-level flag (dashboard panel — no per-collector resolution, no levers here).
              The server re-resolves this collector's fase and books the minprijs itself. */}
          {canBillToPo && !collector.geleverd?.flagged && collector.geleverdWerf && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              Deze werf is gemarkeerd als &ldquo;Collectoren geleverd&rdquo; — collectoren van een
              gemarkeerde fase worden automatisch als &ldquo;reeds geplaatst&rdquo; (minprijs) op de
              PO gezet.
            </div>
          )}

          {/* Pricing levers (foto-controle only): placement charge + supplier-tier override. */}
          {showPricingLevers && (
            <div className="mt-3 rounded-lg border border-gray-200 px-4 py-3 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={geplaatst}
                  onChange={(e) => setGeplaatst(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-td-blue focus:ring-td-blue"
                />
                Al geplaatst <span className="text-gray-500">(voegt "Collector geplaatst" toe)</span>
              </label>

              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1">
                  Tariefcategorie
                  <span className="font-normal"> · basis {Math.round(collector.basisM2 ?? 0)} m² ({collector.tierLabel})</span>
                </div>
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                  {(['under200', 'from200'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTier(t)}
                      aria-pressed={tier === t}
                      className={`px-3 py-1.5 font-medium transition ${
                        tier === t ? 'bg-td-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t === 'under200' ? '0-200 m²' : '200+ m²'}
                    </button>
                  ))}
                </div>
                {tier !== collector.tier && (
                  <p className="mt-1 text-xs text-amber-700">Tarief handmatig aangepast t.o.v. de berekende categorie.</p>
                )}
              </div>
            </div>
          )}

          {resultBanner && (
            <div
              className={`mt-3 rounded-lg px-4 py-3 text-sm ${
                resultBanner.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-amber-50 border border-amber-200 text-amber-800'
              }`}
              role="alert"
            >
              {resultBanner.message}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-60"
          >
            Annuleren
          </button>
          <div className="flex items-center gap-2">
            {canBillToPo && (
              <button
                type="button"
                onClick={handleApproveOnly}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-60"
              >
                Enkel goedkeuren
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition disabled:opacity-60 inline-flex items-center gap-2"
            >
              {submitting && (
              <svg
                className="animate-spin h-4 w-4"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
              {submitting ? 'Bezig…' : 'Bevestigen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
